import { action, computed, observable } from 'mobx';
import { isInSubnet } from 'core/util/ip';
import type { BgpPrefix, ReachabilityTimeseriesResponse, EnrichedASPathGraphSeries } from './bgpRequests';
import { fetchBgpSubPrefixes, fetchBgpReachabilityTimeseries, fetchBgpASPathGraphTimeseries } from './bgpRequests';

/** Minimal root store shape for cross-store access */
interface RootStore {
  $colors: {
    qualitativeColors: string[];
  };
}

export interface FetchSubPrefixesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
}

export interface FetchReachabilityTimeseriesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
  vpKeys?: string[];
  groupBy?: number[];
}

export interface FetchASPathGraphTimeseriesParams {
  prefixes: BgpPrefix[];
  includeCovered?: boolean;
  startDate?: number;
  endDate?: number;
  vpKeys?: string[];
  pruningPercentage?: number;
}

class BgpStore {
  /** Set by root store constructor — provides cross-store access */
  store!: RootStore;

  @observable
  subPrefixesLoading = false;

  @observable
  subPrefixesData: BgpPrefix[] | null = null;

  @observable
  subPrefixesError: Error | null = null;

  @observable
  reachabilityTimeseriesLoading = false;

  @observable
  reachabilityTimeseriesData: ReachabilityTimeseriesResponse | null = null;

  @observable
  reachabilityTimeseriesError: Error | null = null;

  @observable
  asPathGraphTimeseriesLoading = false;

  @observable
  asPathGraphTimeseriesData: EnrichedASPathGraphSeries | null = null;

  @observable
  asPathGraphTimeseriesError: Error | null = null;

  @observable
  asnColorMap = observable.map<string, string>();

  // Frozen AS-path column-vertical adjacency (Tier 3, backup). Kept from the layout with the most
  // vertical edges the PathGraph has reported for the current data load, so colors stay stable
  // while scrubbing (each snapshot re-lays-out).
  private pathGraphVerticalEdges: [string, string][] = [];

  @action
  reset(): void {
    this.subPrefixesLoading = false;
    this.subPrefixesData = null;
    this.subPrefixesError = null;
    this.reachabilityTimeseriesLoading = false;
    this.reachabilityTimeseriesData = null;
    this.reachabilityTimeseriesError = null;
    this.asPathGraphTimeseriesLoading = false;
    this.asPathGraphTimeseriesData = null;
    this.asPathGraphTimeseriesError = null;
    this.asnColorMap.clear();
    this.pathGraphVerticalEdges = [];
  }

  @action
  setSubPrefixesLoading(loading: boolean): void {
    this.subPrefixesLoading = loading;
  }

  @action
  setSubPrefixesData(data: BgpPrefix[]): void {
    this.subPrefixesData = data.map((pfx) => pfx.trim());
    this.subPrefixesError = null;
  }

  @action
  setSubPrefixesError(error: Error): void {
    this.subPrefixesError = error;
    this.subPrefixesData = null;
  }

  @action
  setReachabilityTimeseriesLoading(loading: boolean): void {
    this.reachabilityTimeseriesLoading = loading;
  }

  @action
  setReachabilityTimeseriesData(data: ReachabilityTimeseriesResponse): void {
    this.reachabilityTimeseriesData = data;
    this.reachabilityTimeseriesError = null;

    // Recompute the shared ASN color map now that band adjacency may have changed
    this.recomputeAsnColors();
  }

  @action
  setReachabilityTimeseriesError(error: Error): void {
    this.reachabilityTimeseriesError = error;
    this.reachabilityTimeseriesData = null;
  }

  @action
  setASPathGraphTimeseriesLoading(loading: boolean): void {
    this.asPathGraphTimeseriesLoading = loading;
  }

  @action
  setASPathGraphTimeseriesData(data: EnrichedASPathGraphSeries): void {
    this.asPathGraphTimeseriesData = data;
    this.asPathGraphTimeseriesError = null;

    // Recompute the shared ASN color map now that AS-path adjacency is available
    this.recomputeAsnColors();
  }

  @action
  setASPathGraphTimeseriesError(error: Error): void {
    this.asPathGraphTimeseriesError = error;
    this.asPathGraphTimeseriesData = null;
  }

  @computed
  get hasSubPrefixes(): boolean {
    return Boolean(this.subPrefixesData && this.subPrefixesData.length > 0);
  }

  @computed
  get subPrefixesByPrefix() {
    return (prefix: string): BgpPrefix[] => {
      if (!this.subPrefixesData || this.subPrefixesData.length === 0) {
        return [];
      }

      return this.subPrefixesData.filter((subPfx) => isInSubnet(subPfx, prefix));
    };
  }

  @computed
  get isLoading(): boolean {
    return this.subPrefixesLoading || this.reachabilityTimeseriesLoading || this.asPathGraphTimeseriesLoading;
  }

  /**
   * Store column-vertical adjacency reported by the AS Path Visualization (Tier 3, backup).
   *
   * Only the layout with the **most** vertical edges seen for the current data load is kept, so
   * scrubbing the timeline (which re-lays-out per snapshot) never changes colors and edgeless
   * snapshots are ignored. Triggers a deterministic recompute when a denser layout arrives.
   */
  @action
  setPathGraphVerticalAdjacency(edges: [string, string][]): void {
    if (edges.length <= this.pathGraphVerticalEdges.length) {
      return;
    }
    this.pathGraphVerticalEdges = edges;
    this.recomputeAsnColors();
  }

  /** Add an undirected edge to an adjacency map (skips empty ids and self-loops). */
  private static addAdjacencyEdge(adjacency: Map<string, Set<string>>, a: string, b: string): void {
    if (!a || !b || a === b) {
      return;
    }
    const na = adjacency.get(a) ?? new Set<string>();
    na.add(b);
    adjacency.set(a, na);
    const nb = adjacency.get(b) ?? new Set<string>();
    nb.add(a);
    adjacency.set(b, nb);
  }

  /**
   * Tier 1 (hard): reachability band adjacency. Two ASNs are adjacent when their series are
   * consecutive in timeseries[prefix][groupBy] — i.e. their stacked bands touch. Built for all
   * three ASN groupings so any groupBy toggle is safe.
   */
  private buildReachabilityAdjacency(): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();
    const timeseries = this.reachabilityTimeseriesData?.timeseries;
    if (timeseries) {
      const groupings = ['origin_asn', 'upstream_asn', 'second_upstream_asn'] as const;
      for (const prefixData of Object.values(timeseries)) {
        for (const grouping of groupings) {
          const series = prefixData?.[grouping] ?? [];
          for (let i = 0; i < series.length - 1; i += 1) {
            BgpStore.addAdjacencyEdge(adjacency, series[i].dimension, series[i + 1].dimension);
          }
        }
      }
    }
    return adjacency;
  }

  /**
   * Tier 2 (soft): AS-path direct links. Two ASNs are adjacent when they appear consecutively in
   * any observed as_path (union over all routes → stable, timestamp-independent).
   */
  private buildAsPathAdjacency(): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();
    const routes = this.asPathGraphTimeseriesData?.routes;
    if (routes) {
      for (const route of Object.values(routes)) {
        const path = route?.as_path ?? [];
        for (let i = 0; i < path.length - 1; i += 1) {
          BgpStore.addAdjacencyEdge(adjacency, String(path[i]), String(path[i + 1]));
        }
      }
    }
    return adjacency;
  }

  /** Tier 3 (backup): frozen column-vertical adjacency reported by the PathGraph. */
  private buildVerticalAdjacency(): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();
    for (const [a, b] of this.pathGraphVerticalEdges) {
      BgpStore.addAdjacencyEdge(adjacency, a, b);
    }
    return adjacency;
  }

  /**
   * Determine the order ASNs are colored in. Walk the reachability stacks first (so the critical
   * stacked view alternates cleanly), then any remaining ASNs in numeric order.
   */
  private buildColoringOrder(asnSet: Set<string>): string[] {
    const order: string[] = [];
    const seen = new Set<string>();
    const push = (id: string): void => {
      if (asnSet.has(id) && !seen.has(id)) {
        seen.add(id);
        order.push(id);
      }
    };

    const timeseries = this.reachabilityTimeseriesData?.timeseries;
    if (timeseries) {
      const groupings = ['origin_asn', 'upstream_asn', 'second_upstream_asn'] as const;
      for (const prefixData of Object.values(timeseries)) {
        for (const grouping of groupings) {
          for (const entry of prefixData?.[grouping] ?? []) {
            push(entry.dimension);
          }
        }
      }
    }

    [...asnSet]
      .filter((id) => !seen.has(id))
      .sort((a, b) => Number(a) - Number(b))
      .forEach((id) => order.push(id));

    return order;
  }

  /** Lexicographic less-than for equal-length numeric tuples. */
  private static lexLess(a: number[], b: number[]): boolean {
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return a[i] < b[i];
      }
    }
    return false;
  }

  /**
   * Recompute the shared ASN -> color map using existing palette colors only.
   *
   * Deterministic, adjacency-aware greedy coloring with three strictly prioritised tiers:
   *   Tier 1 reachability band adjacency (hard — never traded away),
   *   Tier 2 AS-path direct links (soft),
   *   Tier 3 column-vertical adjacency (backup).
   * Each ASN takes the palette color with the lowest lexicographic cost
   * [tier1Conflicts, tier2Conflicts, tier3Conflicts, usageCount, index]. Because tier-1 conflicts
   * dominate, a reachability pair only ever shares a color when every color collides (genuinely
   * unavoidable), so lower tiers can never override reachability. usageCount spreads unavoidable
   * reuse across the palette; index keeps it deterministic. Result is shared by both views.
   */
  @action
  private recomputeAsnColors(): void {
    const { qualitativeColors } = this.store.$colors;
    if (!qualitativeColors || qualitativeColors.length === 0) {
      return;
    }
    const palette = qualitativeColors;

    const asnSet = new Set<string>();
    const reachAsns = this.reachabilityTimeseriesData?.lookups?.asns;
    if (reachAsns) {
      Object.keys(reachAsns).forEach((id) => asnSet.add(id));
    }
    const aspathAsns = this.asPathGraphTimeseriesData?.lookups?.asns;
    if (aspathAsns) {
      Object.keys(aspathAsns).forEach((id) => asnSet.add(id));
    }
    if (asnSet.size === 0) {
      return;
    }

    const tier1 = this.buildReachabilityAdjacency();
    const tier2 = this.buildAsPathAdjacency();
    const tier3 = this.buildVerticalAdjacency();
    const order = this.buildColoringOrder(asnSet);

    const assigned = new Map<string, string>();
    const usage = new Array<number>(palette.length).fill(0);

    const conflicts = (neighbors: Set<string> | undefined, color: string): number => {
      if (!neighbors) {
        return 0;
      }
      let count = 0;
      neighbors.forEach((n) => {
        if (assigned.get(n) === color) {
          count += 1;
        }
      });
      return count;
    };

    for (const id of order) {
      const n1 = tier1.get(id);
      const n2 = tier2.get(id);
      const n3 = tier3.get(id);

      let bestIndex = 0;
      let bestCost: [number, number, number, number, number] | null = null;
      for (let ci = 0; ci < palette.length; ci += 1) {
        const color = palette[ci];
        const cost: [number, number, number, number, number] = [
          conflicts(n1, color),
          conflicts(n2, color),
          conflicts(n3, color),
          usage[ci],
          ci
        ];
        if (bestCost === null || BgpStore.lexLess(cost, bestCost)) {
          bestCost = cost;
          bestIndex = ci;
        }
      }

      assigned.set(id, palette[bestIndex]);
      usage[bestIndex] += 1;
    }

    this.asnColorMap.replace(assigned);
  }

  /**
   * Fetch sub-prefixes for the given monitored prefixes.
   * Sets subPrefixesData/subPrefixesError and manages loading state.
   */
  @action
  async fetchSubPrefixes({
    prefixes,
    includeCovered,
    startDate,
    endDate
  }: FetchSubPrefixesParams): Promise<BgpPrefix[]> {
    this.setSubPrefixesLoading(true);

    try {
      const data = await fetchBgpSubPrefixes({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate
      });

      this.setSubPrefixesData(data);
      return data;
    } catch (error) {
      this.setSubPrefixesError(error as Error);
      throw error;
    } finally {
      this.setSubPrefixesLoading(false);
    }
  }

  /**
   * Fetch reachability timeseries for the given prefixes.
   * Sets reachabilityTimeseriesData/Error, registers new ASNs in the color map.
   */
  @action
  async fetchReachabilityTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys
  }: FetchReachabilityTimeseriesParams): Promise<ReachabilityTimeseriesResponse> {
    this.setReachabilityTimeseriesLoading(true);

    try {
      const data = await fetchBgpReachabilityTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys
      });

      this.setReachabilityTimeseriesData(data);
      return data;
    } catch (error) {
      this.setReachabilityTimeseriesError(error as Error);
      throw error;
    } finally {
      this.setReachabilityTimeseriesLoading(false);
    }
  }

  /**
   * Fetch AS Path Graph timeseries for the given prefixes.
   * Sets asPathGraphTimeseriesData/Error, registers new ASNs in the color map.
   */
  @action
  async fetchASPathGraphTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys,
    pruningPercentage
  }: FetchASPathGraphTimeseriesParams): Promise<EnrichedASPathGraphSeries> {
    this.setASPathGraphTimeseriesLoading(true);

    try {
      const data = await fetchBgpASPathGraphTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys,
        pruning_percentage: pruningPercentage
      });

      this.setASPathGraphTimeseriesData(data);
      return data;
    } catch (error) {
      this.setASPathGraphTimeseriesError(error as Error);
      throw error;
    } finally {
      this.setASPathGraphTimeseriesLoading(false);
    }
  }
}

export default new BgpStore();
